// theme-by-host.service.ts
import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

type BrandKey = 'digitopay' | 'lotep' | 'default';

interface HostThemeColors {
  primaryBg: string;
  primaryBgHover: string;
  primaryBgBorder: string;

  primaryBtn: string;
  primaryText: string;
  secondaryBg: string;
  tertiaryBg: string;

  weeklyChartPrimary: string;
  weeklyChartSecondary: string;

  monthlyChartPrimary: string;
  monthlyChartSecondary: string;
  monthlyChartTertiary: string;
}

interface HostThemeConfig {
  colors: HostThemeColors;
  logo: string;
  solutionsLogo: string;
}

@Injectable({ providedIn: 'root' })
export class HostThemeService {
  private readonly themes: Record<BrandKey, HostThemeConfig> = {
    digitopay: {
      colors: {
        primaryBg: '#ffc107',
        primaryBgHover: '#0052cc',
        primaryBgBorder: '#21c44c',

        primaryBtn: '#ffc107',
        primaryText: '#45852b',
        secondaryBg: '#ff5722',
        tertiaryBg: '#9c27b0',

        weeklyChartPrimary: '#0052cc',
        weeklyChartSecondary: '#21c44c',

        monthlyChartPrimary: '#ffc107',
        monthlyChartSecondary: '#ff5722',
        monthlyChartTertiary: '#9c27b0',
      },
      logo: 'assets/images/brand/logo.png',
      solutionsLogo: 'assets/images/brand/harpia.png'
    },

    lotep: {
      colors: {
        primaryBg: '#e38588',
        primaryBgHover: '#cc0000',
        primaryBgBorder: '#cc0000',

        primaryBtn: '#e92428',
        primaryText: '#000',
        secondaryBg: '#b3b3b3',
        tertiaryBg: '#a2cae8',

        weeklyChartPrimary: '#1F7A5C',
        weeklyChartSecondary: '#cc0000',

        monthlyChartPrimary: '#a0a0a0',
        monthlyChartSecondary: '#1f3f57',
        monthlyChartTertiary: '#1f7a5c',
      },
      logo: 'assets/images/brand/lotep.png',
      solutionsLogo: 'assets/images/brand/harpia.png'
    },

    default: {
      colors: {
        primaryBg: '#0052cc',
        primaryBgHover: '#0e62df',
        primaryBgBorder: '#0e62df',

        primaryBtn: '#0052cc',
        primaryText: '#45852b',
        secondaryBg: '#0052cc',
        tertiaryBg: '#0052cc',

        weeklyChartPrimary: '#0CC7E0',
        weeklyChartSecondary: '#094FDB',

        monthlyChartPrimary: '#a0a0a0',
        monthlyChartSecondary: '#1f3f57',
        monthlyChartTertiary: '#1F7A5C',
      },
      logo: 'assets/images/brand/logo.png',
      solutionsLogo: 'assets/images/brand/harpia.png'
    },
  };

  private activeConfig: HostThemeConfig = this.themes.default;

  constructor(@Inject(DOCUMENT) private document: Document) { }

  initTheme(): void {
    const host = (this.document.defaultView?.location.hostname || '').toLowerCase();
    const brand = this.resolveBrand(host);

    this.activeConfig = this.themes[brand];
    this.applyColors(this.activeConfig.colors);
  }

  private resolveBrand(host: string): BrandKey {
    if (host.startsWith('localhost')) {
      // teste de host em dev
      return 'lotep';
    }

    if (host.includes('lotep')) {
      return 'lotep';
    }

    if (host.includes('digitopay')) {
      return 'digitopay';
    }

    return 'default';
  }

  private applyColors(colors: HostThemeColors): void {
    const root = this.document.documentElement;

    root.style.setProperty('--primary-bg-color', colors.primaryBg);
    root.style.setProperty('--primary-bg-hover', colors.primaryBgHover);
    root.style.setProperty('--primary-bg-border', colors.primaryBgBorder);

    root.style.setProperty('--primary-btn-color', colors.primaryBtn);
    root.style.setProperty('--primary-text-color', colors.primaryText);
    root.style.setProperty('--secondary-bg-color', colors.secondaryBg);
    root.style.setProperty('--tertiary-bg-color', colors.tertiaryBg);

    root.style.setProperty('--weekly-chart-primary', colors.weeklyChartPrimary);
    root.style.setProperty('--weekly-chart-secondary', colors.weeklyChartSecondary);

    root.style.setProperty('--monthly-chart-primary', colors.monthlyChartPrimary);
    root.style.setProperty('--monthly-chart-secondary', colors.monthlyChartSecondary);
    root.style.setProperty('--monthly-chart-tertiary', colors.monthlyChartTertiary);
  }

  get logo(): string {
    return this.activeConfig.logo;
  }

  get solutionsLogo(): string {
    return this.activeConfig.solutionsLogo;
  }
}
